import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Grid, Spinner } from "theme-ui";
import moment from "moment-timezone";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import H5 from "components/typography/H5";
import P from "components/typography/P";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import { ChevronIcon, PlusIcon } from "components/svg";
import { themevals } from "theme/themevals";
import { mnt, mntStatCard, mntPill, mntProgressTrack, mntProgressFill } from "theme/maintenanceTokens";
import type { MntStatus } from "theme/maintenanceTokens";
import UserContext from "context/User";

import {
  getServicePlans,
  getServicePlanSiteCounts,
  getServicePlanProgress,
  getWorkOrders,
} from "api/ServicePlans";
import CreateMaintenancePlanModal from "./components/CreateMaintenancePlanModal";
import MaintenanceClientIndex from "./MaintenanceClientIndex";

/**
 * MASTER_PLAN v3.4 §3.9 #22 — Maintenance index at /maintenance. This
 * is the PRIMARY product surface for the maintenance module (peer to
 * RFQs / Quotes / Projects / Invoices / POs).
 *
 * SESSION 46 mockup-12 pass (MOCKUP_AUDIT DRIFT-5/6/7, GAP-6):
 *   - DRIFT-5 Progress column replaces the raw COMMITTED count. Reads
 *     `18 of 200 · 9%` (committed-relative when a commitment exists,
 *     else sites-in-plan) with a thin bar. Progress-per-plan is the
 *     reason a cross-client index exists — it says which of N plans
 *     needs attention today. COMMITTED restated a number LOCATIONS
 *     already implied.
 *   - DRIFT-6 row sub-lines: client cell carries a term/ongoing +
 *     open-WO shape line; plan cell carries a batch-count line.
 *   - DRIFT-7 WO callout is a triage summary (ready-for-RFQ vs
 *     unmatched split + oldest-age) not a feature description.
 *   - GAP-6 stat cards carry qualifying sub-lines.
 *
 * Progress numbers come from one bulk `/plans/progress` call for the
 * whole page (not N per-plan getStats). Site counts stay a separate
 * bulk call; both key by plan id.
 *
 * Terminology: "Service Plan" only remains in code identifiers as
 * legacy debt for a separate rename sweep. Every user-visible string
 * on this page says "Maintenance Plan" (or "Plan" contextually).
 */

/**
 * Route entry for `/maintenance`. Role-branches like Projects: staff get
 * the operator cross-client console below; a client gets the lean
 * read-only list of their own plan(s) (`MaintenanceClientIndex`). The
 * operator component (with its WO triage summary, per-plan progress and
 * create-plan modal, all isCompany-only fetches) is never mounted for a
 * client, so none of those calls fire for a client token.
 */
export default function MaintenanceIndex() {
  const { user } = React.useContext(UserContext) as any;
  if (!user) return null;
  if (user.accessLevel !== "isCompany") return <MaintenanceClientIndex />;
  return <OperatorMaintenanceIndex />;
}

function OperatorMaintenanceIndex() {
  const navigate = useNavigate();
  const { user } = React.useContext(UserContext) as any;
  const isAdmin = user?.role !== null && user?.accessLevel === "isCompany";

  const [plans, setPlans] = useState<any[] | null>(null);
  const [siteCounts, setSiteCounts] = useState<Record<string, number>>({});
  const [progress, setProgress] = useState<
    Record<string, { sitesInPlan: number; sitesServiced: number; committed: number | null; openWos: number; yardsYtd: number }>
  >({});
  const [woSummary, setWoSummary] = useState<{
    total: number;
    ready: number;
    unmatched: number;
    oldestAgeDays: number | null;
  }>({ total: 0, ready: 0, unmatched: 0, oldestAgeDays: null });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (user && user.accessLevel !== "isCompany") {
      navigate("/", { replace: true });
    }
    // eslint-disable-next-line
  }, [user?.accessLevel]);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Maintenance";
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);

    const [plansRes, woRes] = await Promise.all([
      getServicePlans({ query: { recordsPer: 100, sortBy: "updatedAt", sortDir: "desc" } }),
      getWorkOrders({ query: { lifecycle: "needs_match" } }),
    ]);

    const items = Array.isArray(plansRes?.items) ? plansRes.items : [];
    setPlans(items);

    // DRIFT-7 — derive a triage summary from the needs-match set:
    // ready-to-convert (matched) vs unmatched, plus oldest age.
    const wos = Array.isArray(woRes) ? woRes : [];
    const ready = wos.filter(
      (w: any) => w.matchStatus === "auto_matched" || w.matchStatus === "manual_matched" || w.status === "matched",
    ).length;
    const unmatched = wos.length - ready;
    let oldestAgeDays: number | null = null;
    for (const w of wos) {
      const received = w.receivedAt || w.createdAt;
      if (!received) continue;
      const age = moment().diff(moment(received), "days");
      if (oldestAgeDays === null || age > oldestAgeDays) oldestAgeDays = age;
    }
    setWoSummary({ total: wos.length, ready, unmatched, oldestAgeDays });

    if (items.length) {
      const planIds = items.map((p: any) => p.id);
      const [counts, prog] = await Promise.all([
        getServicePlanSiteCounts({ data: { planIds } }),
        getServicePlanProgress({ data: { planIds } }),
      ]);
      setSiteCounts(counts || {});
      setProgress(prog || {});
    }

    setLoading(false);
  };

  const openPlans = (plans || []).filter((p) => p.status === "open");
  const totalSites = Object.values(siteCounts).reduce((sum, n) => sum + (n || 0), 0);
  const clientCount = new Set((plans || []).map((p) => p.client?.id).filter(Boolean)).size;
  const totalCommitted = (plans || []).reduce(
    (sum, p) => sum + (p.committedSiteCount || 0),
    0,
  );
  // GAP-5 — Yards YTD across all plans (mockup 12's fourth stat card).
  // Summed from the same bulk progress map that powers the Progress
  // column; each plan's yardsYtd is SUM(service_event.yards) for the
  // current year. Reads 0 until the project-completion listener has
  // written yard-bearing events, which is correct — no fabricated
  // number. (A service event on a multi-plan site would count toward
  // each plan; that's the same rare single-client-per-site edge the
  // rest of the page's per-plan sums accept — open decision #5.)
  const totalYardsYtd = Object.values(progress).reduce(
    (sum, p) => sum + (p?.yardsYtd || 0),
    0,
  );
  const completedSites = Object.values(progress).reduce(
    (sum, p) => sum + (p?.sitesServiced || 0),
    0,
  );

  if (loading) {
    return (
      <>
        <Header></Header>
        <Page title="Maintenance">
          <Box sx={{ textAlign: "center", paddingY: "40px" }}>
            <Spinner />
          </Box>
        </Page>
      </>
    );
  }

  return (
    <>
      <Header></Header>
      <Page
        title="Maintenance"
        subtitle="Every Maintenance Plan across every client."
        titleAction={
          isAdmin ? (
            <BasicButton
              type="button"
              onClick={() => setShowCreate(true)}
              $iconHeight="14px"
              $iconWidth="14px"
              keepText={true}
            >
              <PlusIcon fill={"#fff"} />
              New Maintenance Plan
            </BasicButton>
          ) : undefined
        }
      >
        <Grid columns={[2, 4]} gap={"14px"} sx={{ marginBottom: "20px" }}>
          <StatCard
            label="Open Plans"
            value={openPlans.length}
            sub={clientCount > 0 ? `across ${clientCount} client${clientCount === 1 ? "" : "s"}` : "\u00A0"}
          />
          <StatCard
            label="Locations on plans"
            value={totalSites}
            sub={totalCommitted > 0 ? `${totalCommitted} committed near-term` : "\u00A0"}
          />
          <StatCard
            label="Unmatched Work Orders"
            value={woSummary.unmatched}
            variant={woSummary.unmatched > 0 ? "warning" : undefined}
            sub={
              woSummary.total > 0
                ? `${woSummary.total} in queue · ${woSummary.ready} ready for RFQ`
                : "nothing waiting"
            }
          />
          <StatCard
            label="Yards YTD (all plans)"
            value={totalYardsYtd}
            sub={
              completedSites > 0
                ? `across ${completedSites} completed site${completedSites === 1 ? "" : "s"}`
                : "no completed work yet"
            }
          />
        </Grid>

        <Box
          as="button"
          onClick={() => navigate("/maintenance/work-orders")}
          sx={{
            display: "block",
            width: "100%",
            textAlign: "left",
            background: woSummary.total > 0 ? mnt.amberPill : mnt.bgSubtle,
            border: "1px solid",
            borderColor: woSummary.total > 0 ? mnt.amberStroke : "inputOutline",
            borderRadius: "8px",
            padding: "14px 18px",
            marginBottom: "20px",
            cursor: "pointer",
            transition: "background 0.15s",
            "&:hover": {
              background: woSummary.total > 0 ? mnt.amberPill : mnt.bgBeige,
            },
          }}
        >
          <Flex sx={{ justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Span
                fontFamily={"Poppins"}
                fontWeight={600}
                fontSize={"13px"}
                color={woSummary.total > 0 ? mnt.amber : themevals.colors.black}
              >
                {woSummary.total > 0
                  ? `${woSummary.total} work order${woSummary.total === 1 ? "" : "s"} waiting in review`
                  : "Work Order Review Queue"}
              </Span>
              <Subtext
                fontSize={"12px"}
                fontFamily={"Poppins"}
                margin={0}
                marginTop={"2px"}
                color={woSummary.total > 0 ? mnt.amber : themevals.colors.dkgrey}
              >
                {woSummary.total > 0
                  ? [
                      `${woSummary.ready} matched and ready for RFQ`,
                      woSummary.unmatched > 0
                        ? `${woSummary.unmatched} unmatched need Match-or-Create`
                        : null,
                      woSummary.oldestAgeDays !== null
                        ? `oldest received ${woSummary.oldestAgeDays === 0 ? "today" : `${woSummary.oldestAgeDays}d ago`}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  : "Nothing waiting on a match right now."}
              </Subtext>
            </Box>
            <ChevronIcon
              width="10px"
              fill={woSummary.total > 0 ? mnt.amber : themevals.colors.dkgrey}
              transform="rotate(-90deg)"
            />
          </Flex>
        </Box>

        <Card>
          <TableHeaderRow />
          {!plans || plans.length === 0 ? (
            <Box sx={{ padding: "40px 20px", textAlign: "center" }}>
              <Subtext
                fontSize={"14px"}
                fontFamily={"Poppins"}
                fontWeight={600}
                margin={0}
                marginBottom={"6px"}
                color={themevals.colors.black}
              >
                No Maintenance Plans yet.
              </Subtext>
              <P
                fontSize={"13px"}
                color={themevals.colors.dkgrey}
                marginTop={0}
                marginBottom={isAdmin ? "18px" : 0}
              >
                {isAdmin
                  ? "Create one to start organizing a client's sites, work orders, and cadence."
                  : "Nothing to see here yet."}
              </P>
              {isAdmin && (
                <Box sx={{ display: "inline-block" }}>
                  <BasicButton
                    type="button"
                    onClick={() => setShowCreate(true)}
                    $iconHeight="14px"
                    $iconWidth="14px"
                    keepText={true}
                  >
                    <PlusIcon fill={"#fff"} />
                    New Maintenance Plan
                  </BasicButton>
                </Box>
              )}
            </Box>
          ) : (
            plans.map((plan, idx) => (
              <PlanRow
                key={plan.id}
                plan={plan}
                sitesCount={siteCounts[plan.id] || 0}
                progress={progress[plan.id]}
                isLast={idx === plans.length - 1}
                onClick={() => navigate(`/maintenance/plans/${plan.id}`)}
              />
            ))
          )}
        </Card>
      </Page>

      {showCreate && (
        <CreateMaintenancePlanModal
          onClose={() => setShowCreate(false)}
          onCreated={(plan) => {
            setShowCreate(false);
            if (plan?.id) {
              navigate(`/maintenance/plans/${plan.id}`);
            } else {
              load();
            }
          }}
        />
      )}
    </>
  );
}

function StatCard({
  label,
  value,
  sub,
  variant,
}: {
  label: string;
  value: number;
  sub?: string;
  variant?: "warning";
}) {
  return (
    <Box
      sx={{
        // Mockup 12: borderless soft-beige stat card (mntStatCard). Was
        // white-with-border, the main reason the page read colder than
        // the mockup. The amber warning tone is preserved for the
        // unmatched-WO card only.
        ...mntStatCard(variant === "warning" && value > 0 ? "warning" : undefined),
      }}
    >
      <Subtext fontSize={"11px"} fontFamily={"Poppins"} fontWeight={600} margin={0} color={themevals.colors.dkgrey}>
        {label.toUpperCase()}
      </Subtext>
      <H5 fontFamily="Poppins" fontWeight={500} fontSize={"20px"} margin={0} marginTop={"6px"}>
        {typeof value === "number" ? Math.round(value).toLocaleString() : value}
      </H5>
      {sub && (
        <Subtext
          fontSize={"11px"}
          fontFamily={"Poppins"}
          margin={0}
          marginTop={"4px"}
          color={themevals.colors.dkgrey}
        >
          {sub}
        </Subtext>
      )}
    </Box>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        background: "white",
        border: "1px solid",
        borderColor: "inputOutline",
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0px 1px 2px 0px rgba(113, 125, 150, 0.03)",
      }}
    >
      {children}
    </Box>
  );
}

function TableHeaderRow() {
  const cols = [
    { flex: "0 0 26%", label: "PLAN" },
    { flex: "0 0 20%", label: "CLIENT" },
    { flex: "0 0 14%", label: "TERM" },
    { flex: "0 0 10%", label: "LOCATIONS" },
    { flex: "0 0 20%", label: "PROGRESS" },
    { flex: 1, label: "STATUS" },
  ];
  return (
    <Flex
      sx={{
        padding: "12px 20px",
        borderBottom: "1px solid",
        borderColor: "inputOutline",
        background: mnt.bgSubtle,
        gap: "12px",
        alignItems: "center",
      }}
    >
      {cols.map((c, i) => (
        <Box key={i} sx={{ flex: c.flex }}>
          <Subtext fontSize={"11px"} fontFamily={"Poppins"} fontWeight={600} margin={0} color={themevals.colors.dkgrey}>
            {c.label}
          </Subtext>
        </Box>
      ))}
    </Flex>
  );
}

function PlanRow({
  plan,
  sitesCount,
  progress,
  isLast,
  onClick,
}: {
  plan: any;
  sitesCount: number;
  progress?: { sitesInPlan: number; sitesServiced: number; committed: number | null; openWos: number };
  isLast: boolean;
  onClick: () => void;
}) {
  const isTermBased = !!plan.endDate;

  // DRIFT-6 — client sub-line: plan shape at a glance.
  const clientSubline = (() => {
    const parts: string[] = [];
    parts.push(isTermBased ? "Term-based" : "Ongoing");
    const openWos = progress?.openWos ?? 0;
    if (openWos > 0) parts.push(`${openWos} open WO${openWos === 1 ? "" : "s"}`);
    return parts.join(" · ");
  })();

  // GAP-30 (mockup 03 card config lines) — plan-shape summary under the
  // plan name so cadence / intake / notification posture read without
  // opening the plan. Same intent as DRIFT-6's sub-lines, adapted to
  // the shipped table. Each part is omitted when its config is absent,
  // so a bare plan shows just the committed count line (or nothing).
  const configSubline = planConfigSummary(plan);

  // DRIFT-5 — Progress cell. Denominator is the commitment when set,
  // otherwise the plan's current location count. Serviced count comes
  // from the bulk progress call.
  const serviced = progress?.sitesServiced ?? 0;
  const denom =
    progress?.committed != null && progress.committed > 0
      ? progress.committed
      : progress?.sitesInPlan ?? sitesCount;
  const pct = denom > 0 ? Math.round((serviced / denom) * 100) : 0;
  const barPct = Math.max(0, Math.min(100, pct));

  const progressLabel = (() => {
    if (plan.status !== "open") {
      return denom > 0 ? `${serviced} of ${denom} · ${pct}%` : "Closed";
    }
    if (denom === 0) return "No locations yet";
    return `${serviced} of ${denom} · ${pct}%`;
  })();

  // Mockup 12 shows a real status pill per row, not the raw open/closed
  // flag. Everything below is DERIVED from data already on the row — no
  // pace or cadence guessing, which this payload cannot support. The
  // mockup's "On track" / "Due soon" wording would need per-plan pace,
  // so the vocabulary stops at what is honest here.
  const rowStatus: { label: string; tone: MntStatus } = (() => {
    if (plan.status !== "open") return { label: "Closed", tone: "closed" };
    if (denom > 0 && serviced >= denom) return { label: "Complete", tone: "current" };
    if (denom === 0) return { label: "No locations", tone: "ungrouped" };
    if (serviced === 0) return { label: "Not started", tone: "upcoming" };
    return { label: "In progress", tone: "onTrack" };
  })();

  return (
    <Flex
      onClick={onClick}
      sx={{
        alignItems: "center",
        gap: "12px",
        padding: "14px 20px",
        borderBottom: isLast ? "none" : "1px solid",
        borderColor: "inputOutline",
        cursor: "pointer",
        "&:hover": { background: mnt.bgSubtle },
      }}
    >
      {/* Mockup 12 leads with the PLAN, not the client: the plan is the
       * object of this page and the thing you click. The build had the
       * client first, which reads as a client list that happens to have
       * plans in it. */}
      <Box sx={{ flex: "0 0 26%", minWidth: 0 }}>
        <Span
          fontFamily={"Poppins"}
          fontWeight={600}
          fontSize={"13px"}
          sx={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: mnt.link,
          }}
        >
          {plan.name}
        </Span>
        {configSubline ? (
          <Subtext
            fontSize={"11px"}
            fontFamily={"Poppins"}
            margin={0}
            color={mnt.textMuted}
            sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            title={configSubline}
          >
            {configSubline}
          </Subtext>
        ) : null}
      </Box>
      <Box sx={{ flex: "0 0 20%", minWidth: 0 }}>
        <Span
          fontFamily={"Poppins"}
          fontSize={"13px"}
          sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {plan.client?.name || "-"}
        </Span>
        <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
          {clientSubline}
        </Subtext>
      </Box>
      <Box sx={{ flex: "0 0 14%" }}>
        <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} color={themevals.colors.dkgrey}>
          {isTermBased ? moment(plan.endDate).format("MMM D, YYYY") : "Ongoing"}
        </Subtext>
      </Box>
      <Box sx={{ flex: "0 0 10%" }}>
        <Span fontFamily={"Poppins"} fontSize={"13px"}>
          {sitesCount}
        </Span>
        {plan.committedSiteCount ? (
          <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
            {plan.committedSiteCount} committed
          </Subtext>
        ) : null}
      </Box>
      <Box sx={{ flex: "0 0 20%", minWidth: 0, paddingRight: "12px" }}>
        <Box sx={{ ...mntProgressTrack, height: "6px", marginBottom: "5px" }}>
          <Box sx={mntProgressFill(barPct, plan.status === "open")} />
        </Box>
        <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
          {progressLabel}
        </Subtext>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ ...mntPill(rowStatus.tone), fontSize: "10.5px" }}>{rowStatus.label}</Box>
      </Box>
    </Flex>
  );
}

// GAP-30 — compact plan-shape summary for the index row (mockup 03's
// `Annual cadence · Intake CorrigoPro · Notifications all silent` card
// config lines, adapted to the shipped table). Reads only real config
// on the plan entity (serviceTypesConfig, intakeStyle,
// notificationConfig) and OMITS any part that isn't set, so the line
// never fabricates a posture the plan doesn't actually have. Returns
// "" when nothing is configured, which suppresses the sub-line.
function planConfigSummary(plan: any): string {
  const parts: string[] = [];

  // Cadence — shortest recurring cadence across configured service
  // types. ad_hoc/none produce no schedule and contribute no label.
  // Mirrors the backend mulchedCadenceMonths "shortest recurring wins".
  const cadence = recurringCadenceLabel(plan?.serviceTypesConfig);
  if (cadence) parts.push(cadence);

  // Intake style — only surface the work-order-fed (CorrigoPro/EMCOR)
  // shape. Quote-fed is the default/common case; labeling every HOA row
  // "Self-service" is noise. Missing intakeStyle (older row) omits too.
  if (plan?.intakeStyle === "work_order_fed") parts.push("Work-order fed");

  // Notification posture — "All silent" when a config exists but every
  // switch is off (the Corrigo-managed default); "Emails on" when any
  // is enabled. No config at all → omit rather than guess, since an
  // unset matrix isn't the same as "silent by choice."
  const posture = notificationPosture(plan?.notificationConfig);
  if (posture) parts.push(posture);

  return parts.join(" · ");
}

function recurringCadenceLabel(
  config: Array<{ cadence?: string }> | null | undefined,
): string {
  if (!Array.isArray(config)) return "";
  let best: number | null = null;
  for (const row of config) {
    const months = row?.cadence === "annual" ? 12 : row?.cadence === "biennial" ? 24 : null;
    if (months == null) continue;
    if (best == null || months < best) best = months;
  }
  if (best === 12) return "Annual cadence";
  if (best === 24) return "Biennial cadence";
  return "";
}

function notificationPosture(
  config: Record<string, boolean> | null | undefined,
): string {
  if (!config || typeof config !== "object") return "";
  const values = Object.values(config);
  if (values.length === 0) return "";
  return values.some((v) => v === true) ? "Emails on" : "All silent";
}
